import type { RefObject } from 'react'

import { ghostTransform } from './carry'
import { PlaceholderSheet } from './PlaceholderSheet'
import type { PointerSpot } from './pointer'
import { PAGE_ASPECT, SHEET_ASPECT_RATIO } from './strip'
import type { DocumentPage } from './types'

const GHOST_PAGE_WIDTH = 56
const STACK_STEP = 7
const PAGES_SHOWN = 3
const CARRIED_EDGE = 'border-blue-600 shadow-xl ring-1 ring-blue-600'

interface CarryGhostProps {
  from: PointerSpot
  pages: DocumentPage[]
  srcFor: (page: number) => string | undefined
  ghostRef: RefObject<HTMLDivElement | null>
}

// The pages themselves, held under the pointer as a small stack. It is moved by hand from the
// pointer gesture (see carry.ts), so the first paint is the only render it costs.
export const CarryGhost = ({
  from,
  pages,
  srcFor,
  ghostRef,
}: CarryGhostProps) => {
  const shown = pages.slice(0, PAGES_SHOWN)
  const deepest = shown.length - 1
  const spread = deepest * STACK_STEP

  return (
    <div
      ref={ghostRef}
      data-carry-ghost
      style={{ transform: ghostTransform(from) }}
      className="pointer-events-none fixed left-0 top-0 z-50 origin-top-left"
    >
      <div
        className="relative -rotate-3"
        style={{
          width: GHOST_PAGE_WIDTH + spread,
          height: GHOST_PAGE_WIDTH * PAGE_ASPECT + spread,
        }}
      >
        {shown
          .map((page, order) => ({ page, depth: deepest - order }))
          .reverse()
          .map(({ page, depth }) => {
            const src = srcFor(page.number)
            return (
              <div
                key={page.number}
                className="absolute"
                style={{ left: depth * STACK_STEP, top: depth * STACK_STEP }}
              >
                {src ? (
                  <img
                    src={src}
                    alt=""
                    style={{
                      width: GHOST_PAGE_WIDTH,
                      aspectRatio: SHEET_ASPECT_RATIO,
                    }}
                    className={`shrink-0 rounded-sm border bg-white object-contain ${CARRIED_EDGE}`}
                  />
                ) : (
                  <PlaceholderSheet
                    width={GHOST_PAGE_WIDTH}
                    className={CARRIED_EDGE}
                  />
                )}
              </div>
            )
          })}
      </div>

      <span
        data-carry-count
        className="mt-1.5 inline-block rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-medium text-white shadow-md"
      >
        {pages.length === 1 ? '1 page' : `${pages.length} pages`}
      </span>
    </div>
  )
}
